/**
 * FeeScheduleDetail — FM2B/FM2C Fee Schedule Detail + Rules Management
 *
 * Route: /finance/fee-setup/:scheduleId
 * RBAC:
 *   finance.fee_schedule.view  — read schedule + rules
 *   finance.fee_rule.manage    — create/edit/archive rules
 *   finance.fee_schedule.edit  — edit schedule metadata (DRAFT only)
 *   finance.fee_schedule.publish — publish schedule (institution_admin, super_admin)
 *   finance.fee_schedule.archive — supersede schedule (institution_admin, super_admin)
 *   finance.fee_schedule.preview — view configuration preview
 *
 * FM2 GUARDRAILS:
 *   - No FeeAssessment rows created
 *   - No student-match or enrollment inference
 *   - Preview: configuration-only, student match = NOT_AVAILABLE
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Plus, RefreshCw, AlertCircle, Eye,
  ChevronDown, Trash2, Edit2, Archive, CheckCircle,
  Lock, Calendar, DollarSign, FileText, Shield
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/layout/Shell";
import { ResponsivePageContainer } from "../components/shared/ResponsivePageContainer";
import { CapabilityState } from "../components/shared/CapabilityState";
import { FormSection } from "../components/shared/FormSection";
import { FormActions } from "../components/shared/FormActions";
import { Button } from "../components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetFooter,
  SheetTitle, SheetDescription,
} from "../components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

// ---- Permission guards --------------------------------------------------------
const MANAGE_RULE_ROLES = new Set(["finance_manager", "institution_admin", "super_admin", "registrar"]);
const EDIT_SCHEDULE_ROLES = new Set(["finance_manager", "institution_admin", "super_admin"]);
const PUBLISH_ROLES = new Set(["institution_admin", "super_admin"]);
const PREVIEW_ROLES = new Set([
  "registrar", "finance_officer", "finance_manager", "cfo",
  "institution_admin", "super_admin", "programme_manager",
]);

// ---- Status helpers ----------------------------------------------------------
const STATUS_CONFIG = {
  DRAFT:      { label: "Draft",      cls: "bg-slate-700 text-slate-200 border-slate-600" },
  PUBLISHED:  { label: "Published",  cls: "bg-emerald-900 text-emerald-200 border-emerald-700" },
  SUPERSEDED: { label: "Superseded", cls: "bg-slate-800 text-slate-400 border-slate-700" },
};
const RULE_STATUS_CONFIG = {
  DRAFT:    { label: "Draft",    cls: "bg-slate-700 text-slate-300 border-slate-600" },
  ACTIVE:   { label: "Active",   cls: "bg-emerald-900 text-emerald-300 border-emerald-700" },
  INACTIVE: { label: "Inactive", cls: "bg-slate-800 text-slate-400 border-slate-700" },
};
const FEE_TYPE_LABELS = {
  TUITION:      "Tuition",
  REGISTRATION: "Registration",
  EXAM:         "Examination",
  LIBRARY:      "Library",
  HOSTEL:       "Hostel",
  LAB:          "Laboratory",
  OTHER:        "Other",
};

function StatusBadge({ status, config = STATUS_CONFIG }) {
  const cfg = config[status] || config.DRAFT;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function formatAmount(amount, symbol) {
  if (!amount) return "—";
  const num = parseFloat(amount);
  return `${symbol || ""}${num.toLocaleString("en-IN", { minimumFractionDigits: 0 })}`;
}

// ============================================================================
// Rule Sheet (Create / Edit)
// ============================================================================
function RuleSheet({ open, onClose, onSaved, scheduleId, rule, currencies, academicYears, programs, scheduleCurrencyId }) {
  const isEdit = !!rule;
  const [form, setForm] = useState({
    code: "", name: "", fee_type: "TUITION", currency_id: scheduleCurrencyId || "",
    formula_type: "FIXED", amount: "", formula_params: {},
    effective_from: "", effective_to: "",
    applicability: { academic_year_ids: [], program_ids: [], delivery_modes: [], apply_to_all: false },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Seed form from rule when editing
  useEffect(() => {
    if (open) {
      if (isEdit && rule) {
        const crit = rule.applicability_criteria || {};
        setForm({
          code: rule.code || "",
          name: rule.name || "",
          fee_type: rule.fee_type || "TUITION",
          currency_id: rule.currency_id || scheduleCurrencyId || "",
          formula_type: rule.formula_type || "FIXED",
          amount: rule.amount || "",
          formula_params: rule.formula_params || {},
          effective_from: rule.effective_from || "",
          effective_to: rule.effective_to || "",
          applicability: {
            academic_year_ids: crit.academic_year_ids || [],
            program_ids: crit.program_ids || [],
            delivery_modes: crit.delivery_modes || [],
            apply_to_all: crit.apply_to_all || false,
          },
        });
      } else {
        setForm((p) => ({
          ...p,
          code: "", name: "", fee_type: "TUITION", formula_type: "FIXED", amount: "",
          effective_from: "", effective_to: "",
          currency_id: scheduleCurrencyId || p.currency_id,
          applicability: { academic_year_ids: [], program_ids: [], delivery_modes: [], apply_to_all: false },
        }));
      }
      setError(null);
    }
  }, [open, rule, isEdit, scheduleCurrencyId]);

  const setField = (f, v) => setForm((p) => ({ ...p, [f]: v }));
  const setAppl = (f, v) => setForm((p) => ({ ...p, applicability: { ...p.applicability, [f]: v } }));

  const toggleArrayItem = (field, item) => {
    const cur = form.applicability[field] || [];
    const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
    setAppl(field, next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name || !form.fee_type || !form.currency_id || !form.effective_from) {
      setError("Code, Name, Fee Type, Currency, and Effective From are required.");
      return;
    }
    if (form.formula_type === "FIXED" && !form.amount) {
      setError("Amount is required for FIXED formula type.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        code: form.code,
        name: form.name,
        fee_type: form.fee_type,
        currency_id: form.currency_id,
        formula_type: form.formula_type,
        amount: form.formula_type === "FIXED" ? form.amount : undefined,
        formula_params: form.formula_type !== "FIXED" ? form.formula_params : undefined,
        effective_from: form.effective_from,
        effective_to: form.effective_to || undefined,
        applicability_criteria: form.applicability,
      };
      let res;
      if (isEdit) {
        res = await api.put(`/finance/fee-schedules/${scheduleId}/rules/${rule.id}`, payload);
      } else {
        res = await api.post(`/finance/fee-schedules/${scheduleId}/rules`, payload);
      }
      onSaved(res.data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || `Failed to ${isEdit ? "update" : "create"} rule.`);
    } finally {
      setSaving(false);
    }
  };

  const FEE_TYPES = ["TUITION", "REGISTRATION", "EXAM", "LIBRARY", "HOSTEL", "LAB", "OTHER"];
  const FORMULA_TYPES = [
    { code: "FIXED", label: "Fixed Amount" },
    { code: "PERCENTAGE_OF_BASE", label: "Percentage of Base" },
    { code: "PER_CREDIT_HOUR", label: "Per Credit Hour" },
    { code: "PER_MODULE", label: "Per Module" },
  ];
  const DELIVERY_MODES = ["ONSITE", "ONLINE", "HYBRID", "BLENDED"];

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle data-testid={isEdit ? "edit-rule-sheet-title" : "add-rule-sheet-title"}>
            {isEdit ? "Edit Fee Rule" : "Add Fee Rule"}
          </SheetTitle>
          <SheetDescription>
            Rules start as DRAFT. They activate automatically when the schedule is published.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid={isEdit ? "edit-rule-form" : "add-rule-form"}>
          {/* Identity */}
          <FormSection title="Rule Identity">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="rule-code">Code <span className="text-red-500">*</span></Label>
                <Input
                  id="rule-code"
                  data-testid="rule-code-input"
                  placeholder="TUITION-MAIN"
                  value={form.code}
                  onChange={(e) => setField("code", e.target.value.toUpperCase())}
                  maxLength={60}
                  disabled={isEdit}
                />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label htmlFor="rule-name">Name <span className="text-red-500">*</span></Label>
                <Input
                  id="rule-name"
                  data-testid="rule-name-input"
                  placeholder="Tuition Fee — PGP 2026"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  maxLength={200}
                />
              </div>
            </div>
          </FormSection>

          {/* Fee Configuration */}
          <FormSection title="Fee Configuration" description="Type, formula, and amount.">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Fee Type <span className="text-red-500">*</span></Label>
                <Select value={form.fee_type} onValueChange={(v) => setField("fee_type", v)} data-testid="rule-fee-type-select">
                  <SelectTrigger data-testid="rule-fee-type-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FEE_TYPES.map((ft) => (
                      <SelectItem key={ft} value={ft} data-testid={`fee-type-${ft}`}>
                        {FEE_TYPE_LABELS[ft] || ft}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Formula Type</Label>
                <Select value={form.formula_type} onValueChange={(v) => setField("formula_type", v)} data-testid="rule-formula-type-select">
                  <SelectTrigger data-testid="rule-formula-type-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FORMULA_TYPES.map((f) => (
                      <SelectItem key={f.code} value={f.code} data-testid={`formula-type-${f.code}`}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Currency <span className="text-red-500">*</span></Label>
                <Select value={form.currency_id} onValueChange={(v) => setField("currency_id", v)} data-testid="rule-currency-select">
                  <SelectTrigger data-testid="rule-currency-trigger">
                    <SelectValue placeholder="Select currency..." />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`rule-currency-${c.iso_code}`}>
                        {c.iso_code} {c.symbol && `(${c.symbol})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {form.formula_type === "FIXED" && (
                <div className="space-y-1.5">
                  <Label htmlFor="rule-amount">Amount <span className="text-red-500">*</span></Label>
                  <Input
                    id="rule-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid="rule-amount-input"
                    placeholder="0.00"
                    value={form.amount}
                    onChange={(e) => setField("amount", e.target.value)}
                  />
                </div>
              )}

              {form.formula_type === "PERCENTAGE_OF_BASE" && (
                <div className="space-y-1.5">
                  <Label htmlFor="rule-rate">Rate (%) <span className="text-red-500">*</span></Label>
                  <Input
                    id="rule-rate"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    data-testid="rule-rate-input"
                    placeholder="e.g. 5.00"
                    value={form.formula_params?.rate || ""}
                    onChange={(e) => setField("formula_params", { ...form.formula_params, rate: e.target.value })}
                  />
                </div>
              )}
            </div>
          </FormSection>

          {/* Effective Period */}
          <FormSection title="Effective Period">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="rule-eff-from">Effective From <span className="text-red-500">*</span></Label>
                <Input
                  type="date"
                  id="rule-eff-from"
                  data-testid="rule-effective-from-input"
                  value={form.effective_from}
                  onChange={(e) => setField("effective_from", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rule-eff-to">Effective To <span className="text-slate-400 text-xs">(optional)</span></Label>
                <Input
                  type="date"
                  id="rule-eff-to"
                  data-testid="rule-effective-to-input"
                  value={form.effective_to}
                  onChange={(e) => setField("effective_to", e.target.value)}
                  min={form.effective_from}
                />
              </div>
            </div>
          </FormSection>

          {/* Applicability */}
          <FormSection
            title="Applicability"
            description="Leave all selectors empty to apply to all (equivalent to apply_to_all = true)."
          >
            <div className="space-y-4">
              {/* Academic Years */}
              {academicYears.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">Academic Year</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {academicYears.map((ay) => (
                      <label key={ay.id} className="flex items-center gap-2 cursor-pointer" data-testid={`appl-ay-${ay.code}`}>
                        <Checkbox
                          checked={form.applicability.academic_year_ids.includes(ay.id)}
                          onCheckedChange={() => toggleArrayItem("academic_year_ids", ay.id)}
                        />
                        <span className="text-sm">{ay.code}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {academicYears.length === 0 && (
                <CapabilityState state="NOT_CONFIGURED" title="Academic Year" reason="No academic years configured. Applies to all years." compact />
              )}

              {/* Programs */}
              {programs.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">Programs</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-1">
                    {programs.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 cursor-pointer" data-testid={`appl-prog-${p.code}`}>
                        <Checkbox
                          checked={form.applicability.program_ids.includes(p.id)}
                          onCheckedChange={() => toggleArrayItem("program_ids", p.id)}
                        />
                        <span className="text-sm">{p.code}</span>
                        <span className="text-xs text-muted-foreground truncate">{p.title}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {programs.length === 0 && (
                <CapabilityState state="NOT_CONFIGURED" title="Programs" reason="No programs found in canonical catalog." compact />
              )}

              {/* Delivery Modes */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Delivery Mode</Label>
                <div className="grid grid-cols-2 gap-2">
                  {["ONSITE", "ONLINE", "HYBRID", "BLENDED"].map((mode) => (
                    <label key={mode} className="flex items-center gap-2 cursor-pointer" data-testid={`appl-mode-${mode}`}>
                      <Checkbox
                        checked={form.applicability.delivery_modes.includes(mode)}
                        onCheckedChange={() => toggleArrayItem("delivery_modes", mode)}
                      />
                      <span className="text-sm">{mode}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Note on cohort/student_category */}
              <p className="text-xs text-muted-foreground border-l-2 border-slate-700 pl-3">
                Cohort, Student Category, and Residency selectors are not yet available — they require
                canonical authority that is not yet configured in FM2.
              </p>
            </div>
          </FormSection>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded bg-red-950/30 border border-red-900/40 text-red-300 text-sm" data-testid="rule-form-error">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
        </form>

        <SheetFooter className="mt-6">
          <FormActions
            variant="dialog"
            onCancel={onClose}
            onSave={handleSubmit}
            saveLabel={isEdit ? "Update Rule" : "Add Rule"}
            saving={saving}
          />
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ============================================================================
// Preview Sheet (FM2E)
// ============================================================================
function PreviewSheet({ open, onClose, scheduleId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !scheduleId) return;
    setLoading(true);
    setError(null);
    api
      .get(`/finance/fee-schedules/${scheduleId}/preview`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load preview."))
      .finally(() => setLoading(false));
  }, [open, scheduleId]);

  const s = data?.schedule;
  const totals = data?.totals;
  const match = data?.student_match;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle data-testid="preview-sheet-title">Configuration Preview</SheetTitle>
          <SheetDescription>
            FM2E: Read-only configuration view. No assessments are created.
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="flex justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 rounded bg-red-950/30 border border-red-900/40 text-red-300 text-sm" data-testid="preview-error">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {data && (
          <div className="space-y-5" data-testid="preview-content">
            {/* Schedule Summary */}
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Schedule</span>
                <StatusBadge status={s?.status} />
              </div>
              <p className="font-semibold text-foreground">{s?.name}</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <span>Code: <strong className="text-foreground font-mono">{s?.code}</strong></span>
                <span>Currency: <strong className="text-foreground">{s?.currency?.iso_code} {s?.currency?.symbol}</strong></span>
                {s?.academic_year && (
                  <span>Academic Year: <strong className="text-foreground">{s.academic_year.code}</strong></span>
                )}
                <span>Effective: <strong className="text-foreground">{s?.effective_from || "—"}</strong></span>
              </div>
            </div>

            {/* Rules */}
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                Rules ({data.rule_count})
              </p>
              {data.rules.length === 0 && (
                <p className="text-sm text-muted-foreground">No rules configured.</p>
              )}
              {data.rules.map((r) => (
                <div key={r.id} className="flex items-center justify-between py-2 px-3 rounded bg-muted/20 border border-border" data-testid={`preview-rule-${r.code}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{r.name}</p>
                    <p className="text-xs text-muted-foreground">{FEE_TYPE_LABELS[r.fee_type] || r.fee_type} · {r.formula_type}</p>
                  </div>
                  <div className="text-right shrink-0 ml-4">
                    {r.formula_type === "FIXED" && r.amount ? (
                      <p className="text-sm font-semibold text-foreground">
                        {s?.currency?.symbol}{parseFloat(r.amount).toLocaleString("en-IN")}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">{r.formula_type}</p>
                    )}
                    <StatusBadge status={r.status} config={RULE_STATUS_CONFIG} />
                  </div>
                </div>
              ))}
            </div>

            {/* Totals */}
            {totals && (
              <div className="rounded-lg border border-border p-4 space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Totals (Fixed rules)</p>
                {Object.entries(totals.by_fee_type || {}).map(([type, amt]) => (
                  <div key={type} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{FEE_TYPE_LABELS[type] || type}</span>
                    <span className="font-medium">{s?.currency?.symbol}{parseFloat(amt).toLocaleString("en-IN")}</span>
                  </div>
                ))}
                <div className="flex justify-between font-semibold text-sm border-t border-border pt-2">
                  <span>Grand Total (Fixed)</span>
                  <span>{s?.currency?.symbol}{parseFloat(totals.grand_total_fixed || "0").toLocaleString("en-IN")}</span>
                </div>
                <p className="text-xs text-muted-foreground">{totals.note}</p>
              </div>
            )}

            {/* Student Match — NOT_AVAILABLE */}
            <div className="rounded-lg border border-amber-800/40 bg-amber-950/20 p-4 space-y-2" data-testid="preview-student-match">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-amber-500" />
                <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider">
                  Student Match: {match?.status}
                </p>
              </div>
              <p className="text-xs text-amber-300/80">{match?.reason}</p>
              <p className="text-xs text-amber-300/60 font-mono">FeeAssessment rows created: {match?.assessment_count ?? 0}</p>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ============================================================================
// Main Page
// ============================================================================
export default function FeeScheduleDetail() {
  const { scheduleId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const canManageRules    = MANAGE_RULE_ROLES.has(user?.role);
  const canEditSchedule   = EDIT_SCHEDULE_ROLES.has(user?.role);
  const canPublish        = PUBLISH_ROLES.has(user?.role);
  const canPreview        = PREVIEW_ROLES.has(user?.role);

  const [schedule, setSchedule] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reference data for rule form
  const [currencies, setCurrencies] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [programs, setPrograms] = useState([]);

  // UI state
  const [showAddRule, setShowAddRule] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  const loadSchedule = useCallback(() => {
    if (!scheduleId) return;
    setLoading(true);
    setError(null);
    Promise.all([
      api.get(`/finance/fee-schedules/${scheduleId}`),
      api.get(`/finance/fee-schedules/${scheduleId}/rules`),
    ])
      .then(([sRes, rRes]) => {
        setSchedule(sRes.data);
        setRules(rRes.data.items || []);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Failed to load fee schedule."))
      .finally(() => setLoading(false));
  }, [scheduleId]);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  // Load reference data for rule forms
  useEffect(() => {
    Promise.all([
      api.get("/finance/admin/currencies"),
      api.get("/finance/admin/academic-years"),
      api.get("/finance/admin/programs"),
    ]).then(([curRes, ayRes, pgRes]) => {
      setCurrencies(curRes.data.status === "OK" ? curRes.data.items : []);
      setAcademicYears(ayRes.data.status === "OK" ? ayRes.data.items : []);
      setPrograms(pgRes.data.status === "OK" ? pgRes.data.items : []);
    }).catch(() => {});
  }, []);

  const handlePublish = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await api.patch(`/finance/fee-schedules/${scheduleId}/publish`);
      setConfirmPublish(false);
      loadSchedule();
    } catch (e) {
      setActionError(e?.response?.data?.detail || "Failed to publish schedule.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchive = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await api.patch(`/finance/fee-schedules/${scheduleId}/archive`);
      setConfirmArchive(false);
      loadSchedule();
    } catch (e) {
      setActionError(e?.response?.data?.detail || "Failed to supersede schedule.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchiveRule = async (ruleId) => {
    try {
      await api.patch(`/finance/fee-schedules/${scheduleId}/rules/${ruleId}/archive`);
      loadSchedule();
    } catch (e) {
      setActionError(e?.response?.data?.detail || "Failed to archive rule.");
    }
  };

  if (loading) {
    return (
      <ResponsivePageContainer data-testid="fee-schedule-detail-loading">
        <div className="flex justify-center py-20">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </ResponsivePageContainer>
    );
  }

  if (error) {
    return (
      <ResponsivePageContainer data-testid="fee-schedule-detail-error">
        <div className="flex items-center gap-2 p-4 rounded bg-red-950/30 border border-red-900/40 text-red-300 text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      </ResponsivePageContainer>
    );
  }

  if (!schedule) return null;

  const isDraft     = schedule.status === "DRAFT";
  const isPublished = schedule.status === "PUBLISHED";
  const isSuperseded = schedule.status === "SUPERSEDED";

  return (
    <ResponsivePageContainer variant="wide" data-testid="fee-schedule-detail-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-4" data-testid="fee-schedule-breadcrumb">
        <Link to="/finance/fee-setup" className="hover:text-foreground transition-colors">Fee Setup</Link>
        <span>/</span>
        <span className="text-foreground font-medium">{schedule.code}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-foreground">{schedule.name}</h1>
            <StatusBadge status={schedule.status} />
          </div>
          <p className="text-sm text-muted-foreground font-mono">{schedule.code}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap shrink-0" data-testid="schedule-actions">
          {canPreview && (
            <Button variant="outline" size="sm" onClick={() => setShowPreview(true)} data-testid="preview-btn">
              <Eye className="h-3.5 w-3.5 mr-1.5" />
              Preview
            </Button>
          )}
          {canPublish && isDraft && (
            <Button
              size="sm"
              onClick={() => setConfirmPublish(true)}
              className="bg-emerald-700 hover:bg-emerald-600"
              data-testid="publish-btn"
            >
              <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
              Publish
            </Button>
          )}
          {canPublish && isPublished && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirmArchive(true)}
              data-testid="archive-btn"
            >
              <Archive className="h-3.5 w-3.5 mr-1.5" />
              Supersede
            </Button>
          )}
        </div>
      </div>

      {actionError && (
        <div className="flex items-center gap-2 p-3 rounded bg-red-950/30 border border-red-900/40 text-red-300 text-sm mb-4" data-testid="schedule-action-error">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {actionError}
          <button className="ml-auto text-red-400 hover:text-red-200" onClick={() => setActionError(null)}>×</button>
        </div>
      )}

      {/* Schedule metadata card */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 rounded-lg border border-border p-4 bg-muted/10 mb-6" data-testid="schedule-metadata-card">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Currency</p>
          <p className="font-semibold text-foreground mt-1">
            {schedule.currency?.iso_code} {schedule.currency?.symbol && `(${schedule.currency.symbol})`}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Academic Year</p>
          <p className="font-semibold text-foreground mt-1">
            {schedule.academic_year?.code || <span className="text-muted-foreground text-xs">—</span>}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Effective From</p>
          <p className="font-semibold text-foreground mt-1">{schedule.effective_from || "—"}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Version</p>
          <p className="font-semibold text-foreground mt-1">v{schedule.version}</p>
        </div>
      </div>

      {/* Lifecycle note for non-DRAFT */}
      {(isPublished || isSuperseded) && (
        <div className="flex items-center gap-2 p-3 rounded bg-slate-800/60 border border-slate-700 text-slate-300 text-xs mb-4" data-testid="schedule-lifecycle-note">
          <Lock className="h-3.5 w-3.5 shrink-0 text-slate-500" />
          {isPublished
            ? "This schedule is PUBLISHED and read-only. Rules cannot be added or edited. Only status transitions are permitted."
            : "This schedule is SUPERSEDED and permanently read-only."}
        </div>
      )}

      {/* Fee Rules Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Fee Rules ({rules.length})
          </h2>
          {canManageRules && isDraft && (
            <Button
              size="sm"
              onClick={() => setShowAddRule(true)}
              data-testid="add-rule-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Rule
            </Button>
          )}
        </div>

        {rules.length === 0 && (
          <CapabilityState
            state="NOT_CONFIGURED"
            title="No fee rules"
            reason={isDraft ? "Add fee rules to this schedule before publishing." : "No fee rules were configured for this schedule."}
            data-testid="fee-rules-empty-state"
          />
        )}

        {rules.length > 0 && (
          <div className="rounded-lg border border-border overflow-hidden" data-testid="fee-rules-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/30 border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Code</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider hidden md:table-cell">Formula</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Status</th>
                  {canManageRules && isDraft && (
                    <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rules.map((r) => (
                  <tr key={r.id} className="hover:bg-muted/10 transition-colors" data-testid={`rule-row-${r.code}`}>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-semibold text-slate-300">{r.code}</span>
                    </td>
                    <td className="px-4 py-3 font-medium text-foreground">{r.name}</td>
                    <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                      {FEE_TYPE_LABELS[r.fee_type] || r.fee_type}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden md:table-cell text-xs">{r.formula_type}</td>
                    <td className="px-4 py-3 text-right font-semibold text-foreground">
                      {r.formula_type === "FIXED"
                        ? formatAmount(r.amount, r.currency_symbol)
                        : <span className="text-muted-foreground text-xs">{r.formula_type}</span>}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <StatusBadge status={r.status} config={RULE_STATUS_CONFIG} />
                    </td>
                    {canManageRules && isDraft && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditingRule(r)}
                            className="p-1.5 rounded hover:bg-muted text-slate-400 hover:text-foreground transition-colors"
                            title="Edit rule"
                            data-testid={`edit-rule-btn-${r.code}`}
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleArchiveRule(r.id)}
                            className="p-1.5 rounded hover:bg-red-950 text-slate-400 hover:text-red-400 transition-colors"
                            title="Delete rule"
                            data-testid={`delete-rule-btn-${r.code}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Rule Sheet */}
      <RuleSheet
        open={showAddRule || !!editingRule}
        onClose={() => { setShowAddRule(false); setEditingRule(null); }}
        onSaved={() => { setShowAddRule(false); setEditingRule(null); loadSchedule(); }}
        scheduleId={scheduleId}
        rule={editingRule}
        currencies={currencies}
        academicYears={academicYears}
        programs={programs}
        scheduleCurrencyId={schedule?.currency?.id}
      />

      {/* Preview Sheet */}
      <PreviewSheet
        open={showPreview}
        onClose={() => setShowPreview(false)}
        scheduleId={scheduleId}
      />

      {/* Publish Confirm Dialog */}
      <Dialog open={confirmPublish} onOpenChange={setConfirmPublish}>
        <DialogContent data-testid="confirm-publish-dialog">
          <DialogHeader>
            <DialogTitle>Publish Fee Schedule?</DialogTitle>
            <DialogDescription>
              Publishing "<strong>{schedule.name}</strong>" will make it read-only and activate all
              DRAFT rules. This action requires institution_admin authority and cannot be undone
              without superseding the schedule.
            </DialogDescription>
          </DialogHeader>
          {actionError && (
            <div className="flex items-center gap-2 p-2 rounded bg-red-950/30 border border-red-900/40 text-red-300 text-xs">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {actionError}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmPublish(false)} data-testid="cancel-publish-btn">Cancel</Button>
            <Button
              onClick={handlePublish}
              disabled={actionLoading}
              className="bg-emerald-700 hover:bg-emerald-600"
              data-testid="confirm-publish-btn"
            >
              {actionLoading ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirm Dialog */}
      <Dialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <DialogContent data-testid="confirm-archive-dialog">
          <DialogHeader>
            <DialogTitle>Supersede Fee Schedule?</DialogTitle>
            <DialogDescription>
              Superseding "<strong>{schedule.name}</strong>" will deactivate all rules and make the
              schedule permanently read-only. This is irreversible. Create a new schedule to replace it.
            </DialogDescription>
          </DialogHeader>
          {actionError && (
            <div className="flex items-center gap-2 p-2 rounded bg-red-950/30 border border-red-900/40 text-red-300 text-xs">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {actionError}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmArchive(false)} data-testid="cancel-archive-btn">Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleArchive}
              disabled={actionLoading}
              data-testid="confirm-archive-btn"
            >
              {actionLoading ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Supersede
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ResponsivePageContainer>
  );
}
